import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Account.css'
import { useAuth } from '../../AuthContext';

import { FiUser, FiLock, FiTrash2 } from 'react-icons/fi';

const AccountPage = () => {

    const location = useLocation();
    const [activeForm, setActiveForm] = useState('account');
    const [userInfo, setUserInfo] = useState({ full_name: '', email: '', company: '', newsletter: false });
    const [originalUserInfo, setOriginalUserInfo] = useState({ ...userInfo });
    const [editMode, setEditMode] = useState(false);
    const [error, setError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [confirmation, setConfirmation] = useState('');
    const [logoutError, setLogoutError] = useState('');

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const { dispatch } = useAuth();
    const navigate = useNavigate();
    const { state: { isAuthenticated } } = useAuth();

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    const nameCompanyRegex = /^[a-zA-Z\s.,'-]*$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;


    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                const response = await fetch('https://pharm-rx-backend-50d4ad87e02c.herokuapp.com/api/users/me', {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch user data');
                }

                const data = await response.json();
                setUserInfo({
                    full_name: data.full_name || '',
                    email: data.email || '',
                    company: data.company || '',
                    newsletter: data.newsletter ? data.newsletter : false
                });
            } catch (err) {
                setError(err.message);
            }
        };

        fetchUserInfo();
    }, []);

    useEffect(() => {
        const hash = location.hash;
        switch (hash) {
            case '#password':
                setActiveForm('password');
                break;
            case '#logout':
                setActiveForm('logout');
                break;
            default:
                setActiveForm('account');
        }
    }, [location]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserInfo(prevUserInfo => ({
            ...prevUserInfo,
            [name]: value
        }));
    };

    const handleNewsletterChange = (e) => {
        setUserInfo(prevUserInfo => ({
            ...prevUserInfo,
            newsletter: e.target.checked
        }));
    };

    const handleEditClick = () => {
        setOriginalUserInfo({ ...userInfo }); // Store current user info
        setEditMode(true);
        setError('');
    };
    

    const handleCurrentPasswordChange = (e) => setCurrentPassword(e.target.value);
    const handleNewPasswordChange = (e) => setNewPassword(e.target.value);
    const handleConfirmNewPasswordChange = (e) => setConfirmNewPassword(e.target.value);

    const handlePasswordChangeSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmNewPassword) {
            setError("New passwords do not match.");
            return;
        }

        if (!passwordRegex.test(newPassword)) {
            setError('Password must be at least 8 characters long and include numbers, uppercase and lowercase letters, and special characters like @$!%*?&.');
            return;
        }

        try {
            const response = await fetch('https://pharm-rx-backend-50d4ad87e02c.herokuapp.com/api/change-password/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
            });

            if (!response.ok) {
                throw new Error('Password change failed');
            }

            const data = await response.json();
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            alert(data.message);
        } catch (error) {
            console.error('Error changing password:', error);
            alert(error.message);
        }
    };


    const handleDetailsSubmit = async (e) => {
        e.preventDefault();

        if (!emailRegex.test(userInfo.email)) {
            setError('Please enter a valid email. Example: user@example.com.');
            return;
        }
    
        if (!nameCompanyRegex.test(userInfo.full_name) || !nameCompanyRegex.test(userInfo.company)) {
            setError("Name or company name contains unallowed characters.");
            return;
        }
        try {
            const response = await fetch('https://pharm-rx-backend-50d4ad87e02c.herokuapp.com/api/update-user-details/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(userInfo)
            });

            if (!response.ok) {
                throw new Error('Failed to update user details');
            }

            const data = await response.json();
            setConfirmation(data.message);
            setEditMode(false);
        } catch (error) {
            console.error('Error updating user details:', error);
            alert('Error updating user details: ' + error.message);
        }
    };

    const handleLogout = async () => {
        try {
            const response = await fetch('https://pharm-rx-backend-50d4ad87e02c.herokuapp.com/api/logout', {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                console.log('Logout successful');
                dispatch({ type: 'LOGOUT' });
                navigate('/');
                // Redirect user to homepage or login page
            } else {
                console.error('Logout failed');
                setLogoutError('Logout failed. Please try again.')
            }
        } catch (error) {
            console.error('Error during logout:', error);
            setLogoutError('An error occured during logout.')
        }
    };

    const handleCancelClick = (() => {
        setUserInfo({ ...originalUserInfo });
        setEditMode(false);
        setError('');

    })

    const handleSetAccountForm = () => {
        setError('');
        setPasswordError('');
        setLogoutError('');
        setConfirmation('');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setActiveForm('account');
    }

    const handleSetPasswordForm = () => {
        setError('');
        setLogoutError('');
        setEditMode(false);
        setConfirmation('');
        setActiveForm('password');
    }

    const handleSetLogoutForm = () => {
        setError('');
        setLogoutError('');
        setPasswordError('');
        setEditMode(false);
        setConfirmation('');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setActiveForm('logout');
        
    }


    if (!isAuthenticated) {
        const handleLoginClick = () => {
            navigate('/?login=true');
        };

        return (
            <div className='main-container'>
                <div className='account-font-wrapper'>
                    <div className="unauthenticated-message">
                        <p>You aren't signed in. Please<button className='unauth-redirect-button' onClick={handleLoginClick}>click here</button>to sign in.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='main-container'>
            <div className='account-font-wrapper'>
                <div className="account-container">
                    <div className="account-header">
                        <h2 className="account-title">Account Settings</h2>
                        <p>Change your account settings</p>
                    </div>
                    <div className="account-layout">
                        <div className="account-sidebar">
                            <ul className="sidebar-menu">
                                <li className="sidebar-item" onClick={handleSetAccountForm}><FiUser /> Account</li>
                                <li className="sidebar-item" onClick={handleSetPasswordForm}><FiLock /> Password</li>
                                <li className="sidebar-item" onClick={handleSetLogoutForm}><FiTrash2 /> Logout</li>
                                {/* <li className="sidebar-item">Security</li> */}
                            </ul>
                        </div>
                        {userInfo && (
                            <div>
                                {error && <div className='account-error-message'>{error}</div>}
                                {confirmation && <div className='account-confirmation-message'>{confirmation}</div>}
                                {activeForm === 'account' && !editMode && (
                                    <div className="profile-info">
                                        <h5 className='profile-detail-title'>Account Info</h5>
                                        <div className="profile-detail">
                                            <span className="detail-label">Name</span>
                                            <span className="detail-value">{userInfo.full_name}</span>
                                        </div>
                                        <div className="profile-detail">
                                            <span className="detail-label">Email</span>
                                            <span className="detail-value">{userInfo.email}</span>
                                        </div>
                                        <div className="profile-detail">
                                            <span className="detail-label">Company</span>
                                            <span className="detail-value">{userInfo.company}</span>
                                        </div>
                                        <div className="profile-detail">
                                            <span className="detail-label">Newsletter Subscription</span>
                                            <input
                                                type="checkbox"
                                                className='detail-value-checkbox'
                                                checked={userInfo.newsletter}
                                                disabled
                                            />
                                        </div>
                                        <button className="edit-button" onClick={handleEditClick} >Edit Profile</button>
                                    </div>
                                )}
                                {activeForm === 'account' && editMode && (
                                    <form className="edit-form" onSubmit={handleDetailsSubmit}>
                                        {/* Form fields here with labels above */}
                                        <h5 className='edit-profile-title'>Edit Account Info</h5>
                                        <div className="form-group">
                                            <label className="form-label">Name</label>
                                            <input className="form-input" type="text" name="full_name" value={userInfo.full_name} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Email</label>
                                            <input className="form-input" type="email" name="email" value={userInfo.email} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Company</label>
                                            <input className="form-input" type="text" name="company" value={userInfo.company} onChange={handleChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Newsletter Subscription</label>
                                            <input className="form-input-checkbox" type="checkbox" name="newsletter" checked={userInfo.newsletter} onChange={handleNewsletterChange} />
                                        </div>
                                        <button className="save-button" type="submit">Save Changes</button>
                                        <button className="cancel-button" onClick={handleCancelClick} >Cancel</button>
                                    </form>
                                )}
                                {passwordError && <div className='password-error-message'>{error}</div>}
                                {activeForm === 'password' && (

                                    <form className="password-reset-form" onSubmit={handlePasswordChangeSubmit}>
                                        <h5 className='edit-password-title'>Change Password</h5>
                                        <div className="form-group">
                                            <label className="form-label">Current Password</label>
                                            <input className="form-input" type="password" name="currentPassword" value={currentPassword} onChange={handleCurrentPasswordChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">New Password</label>
                                            <input className="form-input" type="password" name="newPassword" value={newPassword} onChange={handleNewPasswordChange} />
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Confirm New Password</label>
                                            <input className="form-input" type="password" name="confirmNewPassword" value={confirmNewPassword} onChange={handleConfirmNewPasswordChange} />
                                        </div>
                                        <button className="save-button" type="submit">Change Password</button>
                                    </form>
                                )}
                                {logoutError && <div className='logout-error-message'>{error}</div>}
                                {activeForm === 'logout' && (
                                    <div className="logout-confirmation">
                                        <p>Are you sure you want to log out?</p>
                                        <button className="logout-button" onClick={handleLogout}>Confirm Logout</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountPage;
